import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth, roleHome } from "../AuthContext";
import Brand from "../../components/site/Brand";

export default function Signup() {
  const { signup, demoLogin, status, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    business_name: "",
    name: "",
    email: "",
    password: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // If already signed in, send them to their proper home — not blindly to
  // /app/dashboard (which staff can't access).
  if (status === "authed") return <Navigate to={roleHome(user)} replace />;

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await signup(form);
      navigate(roleHome(data?.user));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F6F5F0] flex items-center justify-center px-5 py-10" data-testid="signup-page">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Brand tone="dark" />
        </div>

        <div className="bg-white rounded-2xl shadow-[0_20px_50px_-25px_rgba(11,18,32,0.25)] border border-slate-200 p-8">
          <h1 className="font-display text-2xl md:text-3xl tracking-tight text-slate-900 font-semibold">
            Start your compliance workspace.
          </h1>
          <p className="mt-2 text-slate-500 text-sm">
            Free to try. Add your team and documents in minutes.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <Field
              label="Business name"
              testid="signup-business"
              value={form.business_name}
              onChange={(v) => update("business_name", v)}
              placeholder="e.g. GreenLeaf Medical Cannabis"
              required
            />
            <Field
              label="Your name"
              testid="signup-name"
              value={form.name}
              onChange={(v) => update("name", v)}
              placeholder="Alex Turner"
              required
            />
            <Field
              label="Work email"
              type="email"
              testid="signup-email"
              value={form.email}
              onChange={(v) => update("email", v)}
              placeholder="you@yourbusiness.co.uk"
              required
            />
            <Field
              label="Password"
              type="password"
              testid="signup-password"
              value={form.password}
              onChange={(v) => update("password", v)}
              placeholder="At least 8 characters"
              minLength={8}
              required
            />

            {error && (
              <div data-testid="signup-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="signup-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B1220] hover:bg-[#131c31] disabled:opacity-40 text-white px-4 py-3 text-sm font-semibold transition-colors"
            >
              {busy ? "Creating your workspace…" : "Create workspace"}
              <ArrowRight size={14} />
            </button>
          </form>

          <button
            onClick={async () => {
              try {
                const data = await demoLogin();
                navigate(roleHome(data?.user));
              } catch (e) { setError(e.message); }
            }}
            data-testid="signup-try-demo"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#D4A65A]/40 bg-[#D4A65A]/10 hover:bg-[#D4A65A]/15 text-[#8C6B33] px-4 py-3 text-sm font-semibold transition-colors"
          >
            <ShieldCheck size={14} /> Or explore the demo first
          </button>
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          Already have an account?{" "}
          <Link to="/app/login" data-testid="signup-goto-login" className="text-[#8C6B33] hover:text-[#0B1220] font-semibold">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", required, placeholder, minLength }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">
        {label}
      </label>
      <input
        type={type}
        required={required}
        minLength={minLength}
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none transition-colors"
      />
    </div>
  );
}
